import { useCallback } from 'react';
import { TreeNode, CommandAction, TreeIndexMap, LogLocation } from '../types';
import { STORAGE_BRANCH } from '../constants';
import { useUI, useEditorState, useLogs, useSettings } from '../contexts';
import { Symbol } from './useSymbols';

type UseAppCallbacksParams = {
  handleOpenFile: (id: string) => void;
  setActiveBranch: (branch: string) => void;
  isDirty: (id: string) => boolean;
  handleSave: () => Promise<boolean>;
  treeIndex: TreeIndexMap;
  setPendingLocation: (loc: LogLocation | null) => void;
  goToEditorLocation: (fileId: string, line: number, column: number) => void;
};

export function useAppCallbacks({
  handleOpenFile,
  setActiveBranch,
  isDirty,
  handleSave,
  treeIndex,
  setPendingLocation,
  goToEditorLocation,
}: UseAppCallbacksParams) {
  // Get UI state from context
  const {
    setGoToFileOpen,
    setGoToQuery,
    setGoToHighlight,
    setCommandPaletteOpen,
    setCommandQuery,
    setCommandHighlight,
    setGlobalSearchOpen,
    setStatus,
  } = useUI();

  // Get editor state from context
  const { commitMessage, setCommitMessage, activeFile } = useEditorState();

  // Get logs from context
  const { addLog } = useLogs();

  // Get settings from context
  const { autoSaveEnabled } = useSettings();
  const handleSelectGoTo = useCallback(
    (node: TreeNode) => {
      setGoToFileOpen(false);
      setGoToQuery('');
      setGoToHighlight(0);
      handleOpenFile(node.id);
    },
    [handleOpenFile, setGoToFileOpen, setGoToQuery, setGoToHighlight]
  );

  const handleSelectCommand = useCallback(
    (cmd: CommandAction) => {
      setCommandPaletteOpen(false);
      setCommandQuery('');
      setCommandHighlight(0);
      cmd.run();
    },
    [setCommandPaletteOpen, setCommandQuery, setCommandHighlight]
  );

  const handleCommit = useCallback(() => {
    const msg = commitMessage.trim();
    if (!msg) {
      setStatus('Commit mesajı girin');
      return;
    }
    addLog({ task: 'commit', level: 'info', message: `Commit: ${msg}` });
    setStatus(`Commit queued · ${msg}`);
    setCommitMessage('');
  }, [addLog, commitMessage, setCommitMessage, setStatus]);

  const handleChangeBranch = useCallback(
    (branch: string) => {
      setActiveBranch(branch);
      localStorage.setItem(STORAGE_BRANCH, branch);
    },
    [setActiveBranch]
  );

  const handleEditorBlur = useCallback(() => {
    if (autoSaveEnabled && activeFile && isDirty(activeFile)) {
      void handleSave();
    }
  }, [autoSaveEnabled, activeFile, isDirty, handleSave]);

  // Global search result selection handler
  const handleGlobalSearchSelect = useCallback(
    (filePath: string, line: number, column: number) => {
      setGlobalSearchOpen(false);

      // Find the node by path
      const node = treeIndex.byPath[filePath] || treeIndex.byId[filePath];

      if (node && node.type === 'file') {
        handleOpenFile(node.id);
        // Set pending location to navigate after file opens
        setPendingLocation({ path: filePath, line, column, fileId: node.id });
      }
    },
    [setGlobalSearchOpen, treeIndex, handleOpenFile, setPendingLocation]
  );

  // Symbol selection handler - navigate to symbol in current file
  const handleSymbolSelect = useCallback(
    (symbol: Symbol) => {
      if (activeFile) {
        goToEditorLocation(activeFile, symbol.line, symbol.column);
      }
    },
    [activeFile, goToEditorLocation]
  );

  return {
    handleSelectGoTo,
    handleSelectCommand,
    handleCommit,
    handleChangeBranch,
    handleEditorBlur,
    handleGlobalSearchSelect,
    handleSymbolSelect,
  };
}
